import axios, { AxiosError, AxiosRequestConfig, AxiosRequestTransformer, AxiosResponseTransformer } from "axios";
import AppEnv from "src/app/AppEnv";
import { DictionnaireApi } from "src/app/api/DictionnaireApi";
import { format } from "date-fns";
import { redirectUrlMep } from "src/app/AppLauncher";

export const gererRedirectSecuriteFor401 = (error: AxiosError) => {

  if (error.response?.status === 401) {

    sessionStorage.setItem(redirectUrlMep, window.location.pathname+window.location.search);

    if (AppEnv.typeAuth === "LDAP") {
      window.location.href = `/login/sso`;
    } else {
      window.location.href = `/auth`;
    }
  } else {
    throw error;
  }

};

export const gererRedirectSecurite = () => {

  sessionStorage.setItem(redirectUrlMep, window.location.pathname+window.location.search);

  if (AppEnv.typeAuth === "LDAP") {
    window.location.href = `/login/sso`;
  } else {
    window.location.href = `/auth`;
  }


};

export const logout = () => {
  const url = DictionnaireApi.logoutURL.getUrl();
  axios.post(url, null);
};

const apiClient = axios.create({
  baseURL: `${AppEnv.urlBack}/api`,
  timeout: !isNaN(parseInt(AppEnv.timeout)) ? parseInt(AppEnv.timeout) : 60000,
  validateStatus: (status) => status === 200,
});

apiClient.interceptors.request.use(
  function(config) {
    console.debug("%cREQ", "color: green; font-weight: bold", config.url, {
      data: config.data,
      config,
    });

    return config;
  },
  function(error: AxiosError) {
    console.error(error);
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    console.log(
      "%cKO",
      "color: red; font-weight: bold",
      error.config?.url,
      error.response && error.response.status,
      error.response && error.response.data ? error.response.data : error.response,
    );
    if (
      error.message === "Network Error" &&
      (!error.config?.headers?.try || (error.config.headers.try && +error.config.headers.try < 5))
    ) {
      setTimeout(() => {
        console.log("%cRetry callAPI", "color: red; font-weight: bold", error.config?.url, error.config?.headers?.try);
        const essais: string = error.config?.headers?.try ? "" + +error.config.headers.try + 1 : "1";
        const newConfig = { ...error.config, headers: { ...error.config?.headers, try: essais } };
        return apiClient.request(newConfig);
      });
    }

    return Promise.reject(error);
  },
);

export type ValidationErrorType = "BANNER" | "FIELD";
export type GenericApiError = {
  errorType: ValidationErrorType;
  errorfieldPath: string;
  errorMessageKey: string;
  errorMessageArgument: string[];
};

export type GenericApiResponse<R> = any | R;

export const parseBooleans = <T extends unknown>(obj: T) => {
  if (obj && obj instanceof Object) {
    const newObj = Array.isArray(obj) ? [...obj] : { ...(obj as object) };
    Object.entries(newObj).forEach(([key, value]) => {
      if (!((value as any) instanceof Date) && (value === true || value === false)) {
        if (value === true) {
          (newObj as any)[key] = "true";
        } else if (value === false) {
          (newObj as any)[key] = "false";
        }
      } else if (!((value as any) instanceof Date) && (value as any) instanceof Object) {
        (newObj as any)[key] = parseBooleans(value);
      }
    });
    return newObj;
  }
  return obj;
};

/**
 * On force le formatage de la date avant de l'envoyer au back.
 * Sinon par defaut c'est date.toJSON() qui est appelé et qui prend en compte la timezone, ce qui peut faire +-1jour à la date envoyée au back
 * https://stackoverflow.com/questions/70689305/customizing-date-serialization-in-axios
 */
const dateTransformer = <Data>(data: Data): any => {
  if (data instanceof Date) {
    return format(data, "yyyy-MM-dd");
  }
  if (Array.isArray(data)) {
    return data.map(val => dateTransformer(val));
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, val]) =>
      [key, dateTransformer(val)]));
  }
  return data;
};

const requestTranformer = <Data>(data: Data) => {
  return dateTransformer(data);
};

const responseTransformer = (jsonData: string) => {
  return jsonData;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const callAppAPI = async <Data extends {} | void = void>(
  config: AxiosRequestConfig,
): Promise<GenericApiResponse<Data>> => {
  const requestConfig = {
    ...config,
    transformRequest: [
      (data: Data) => requestTranformer<Data>(data),
      ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
    ],
    transformResponse: [
      (jsonData: string) => responseTransformer(jsonData),
      ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    ],
  };
  const response = await apiClient.request(requestConfig);

  return response.data as GenericApiResponse<Data>;
};
